import React, { useState } from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AdjustIcon from '@material-ui/icons/Adjust';
import TripOriginIcon from '@material-ui/icons/RadioButtonUnchecked';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentFileItem } from '../../../../types';
import { RootState } from '../../../../../../redux/root';
import { setOpenedTabIndex, setSelectedDocument } from '../../../../../../redux/slices/documentsSlice';
import { DocStatus } from '../../../../../../constants';
import { Loader } from '../../../../../../atoms';

const statusLabel = (status: number) => {
  if (status === DocStatus.COMPLETED) return 'Completed';
  if (status === DocStatus.UNDER_REVIEW) return 'Under review';
  if (status === DocStatus.PENDING) return 'Pending';
  if (status === DocStatus.WAITING_ON_CLIENT) return 'Waiting';
  if (status === DocStatus.REJECTED) return 'Rejected';
  return '';
};

const iconFor = (status: number) => {
  if (status === DocStatus.COMPLETED) return <CheckCircleIcon />;
  if (status === DocStatus.UNDER_REVIEW) return <AdjustIcon />;
  if (status === DocStatus.PENDING) return <AdjustIcon />;
  if (status === DocStatus.WAITING_ON_CLIENT) return <TripOriginIcon />;
  if (status === DocStatus.REJECTED) return <NotInterestedIcon />;
  return null;
};

const FilesSection = () => {
  const permitDocuments = useSelector((s: RootState) => s.documents.PermitDocuments);
  const permitDocumentsLoading = useSelector((s: RootState) => s.documents.permitDocumentsLoading);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const dispatch = useDispatch();

  const onClick = (e, item, index) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndex(index);
    dispatch(setSelectedDocument(item));
    dispatch(setOpenedTabIndex(2));
  };

  if (permitDocumentsLoading && permitDocuments.length === 0) {
    return (
      <LoadingWrap>
        <Loader size={28} thickness={3.5} />
        <LoadingLabel>Loading documents…</LoadingLabel>
      </LoadingWrap>
    );
  }

  return (
    <Files>
      {permitDocuments.length === 0 && <Empty>No documents on this permit.</Empty>}
      {permitDocuments.map((item: DocumentFileItem, index: number) => {
        const count = item.clientFiles && item.clientFiles.length ? item.clientFiles.length : 0;
        const selected = index === selectedIndex;
        return (
          <FileRow key={index} $selected={selected} onClick={e => onClick(e, item, index)}>
            <StatusIcon $status={item.status} title={statusLabel(item.status)}>
              {iconFor(item.status)}
            </StatusIcon>
            <FileName $selected={selected}>{item.name}</FileName>
            <Count $hasFiles={count > 0}>{count}</Count>
          </FileRow>
        );
      })}
    </Files>
  );
};

export default FilesSection;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileRow = styled.button<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary-200, #a1cde6)' : 'transparent')};
  background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'transparent')};
  border-radius: 8px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const iconColor = (status: number) => {
  if (status === DocStatus.COMPLETED) return 'var(--success-500, #2f9e5e)';
  if (status === DocStatus.UNDER_REVIEW) return 'var(--primary-600)';
  if (status === DocStatus.PENDING) return 'var(--primary-600)';
  if (status === DocStatus.WAITING_ON_CLIENT) return 'var(--neutral-400)';
  if (status === DocStatus.REJECTED) return 'var(--danger-500, #e0423f)';
  return 'var(--neutral-400)';
};

export const IconWrapper = styled.div<{ status: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ status }) => iconColor(status)};

  svg {
    font-size: 18px;
  }
`;

const StatusIcon = styled(IconWrapper)`
  width: 22px;
  height: 22px;
`;

const FileName = styled.span<{ $selected: boolean }>`
  font-size: 0.875rem;
  color: ${({ $selected }) => ($selected ? 'var(--primary-700)' : 'var(--mainText)')};
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Count = styled.span<{ $hasFiles: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${({ $hasFiles }) => ($hasFiles ? 'var(--primary-50)' : 'var(--neutral-100)')};
  color: ${({ $hasFiles }) => ($hasFiles ? 'var(--primary-700)' : 'var(--neutral-500)')};
  border: 1px solid ${({ $hasFiles }) => ($hasFiles ? 'var(--primary-100, #cfe6f3)' : 'var(--border)')};
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.8125rem;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 12px;
`;

const LoadingLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  font-weight: 500;
`;
